/**
 * Admin: member directory. Route: /admin/members (Admin role or the Member.Read right).
 *
 * Server-side search, filters, sorting and paging through POST api/members/list-view
 * (memberService.listView). Filters: search, member type, active or inactive, home campus
 * (optionally serve campus too; 0 = Online Campus), has a login, member or visitor, deleted.
 * Rows open /admin/members/:memberId (MemberID, so visitors and children without a login work).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconUsers, IconUserPlus, IconRefresh, IconUserCheck, IconUserQuestion, IconLinkOff, IconLink, IconPlugConnected, IconHeartbeat, IconFilterOff,
  IconListDetails,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Stack, Group, Text, Button, Card, Paper, TextInput, Select, Switch, Badge, Avatar, Alert, SimpleGrid, Tooltip } from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { DataTable, type DataTableColumn } from '@/components/advanced';
import { usePermissions } from '@/components/ccms-components/auth/hooks/usePermissions';
import { usePageFilters } from '@/stores';
import { useMemberService, type MemberFilterRequest, type MemberModelView } from '@actschurch/ccms-sdk';
import {
  MEMBER_DIRECTORY_KEY, useCampuses, useMemberTypes, memberFullName, initialsOf, formatStamp, errorMessage, membershipStatusFor, parseLocationValue,
} from '@/components/members';
import { CreateMemberModal } from './CreateMemberModal';

type Row = MemberModelView & { id: number };
type SortBy = NonNullable<MemberFilterRequest['sortBy']>;

const DEFAULT_FILTERS = {
  search: '' as string,
  typeId: null as string | null,
  status: null as string | null,      // 'active' | 'inactive'
  campus: null as string | null,      // LocationID as a string ("0" = Online Campus)
  includeServe: false,
  login: null as string | null,       // 'yes' | 'no'
  membership: null as string | null,  // 'member' | 'visitor'
  includeDeleted: false,
  pageSize: 25,
  sortBy: 'name' as SortBy,
  sortDescending: false,
};

const SORT_KEYS: Record<string, SortBy> = { name: 'name', memberCode: 'memberCode', createdDateTime: 'createdDateTime', memberID: 'memberId' };

function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function tint(color?: string | null) {
  return color && color.startsWith('#') ? { backgroundColor: `${color}22`, color } : undefined;
}

export default function MemberDirectoryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const memberService = useMemberService();
  const { hasRole, hasPermission } = usePermissions();
  const canCreate = hasRole('Admin') || hasPermission('Member.Write');
  const canManageFields = hasRole('Admin') || hasPermission('Member.CustomFields.Manage');
  const { options: typeOptions } = useMemberTypes();
  const campuses = useCampuses();
  const [filters, setFilter, setFilters, clearFilters] = usePageFilters('admin/members', DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const search = useDebounced(filters.search.trim());

  const request = useMemo<MemberFilterRequest>(() => ({
    search: search || null,
    typeId: filters.typeId ? Number(filters.typeId) : null,
    isActive: filters.status === 'active' ? true : filters.status === 'inactive' ? false : null,
    locationId: parseLocationValue(filters.campus),
    includeServeLocation: filters.campus !== null ? filters.includeServe : null,
    hasLogin: filters.login === 'yes' ? true : filters.login === 'no' ? false : null,
    isMember: filters.membership === 'member' ? true : filters.membership === 'visitor' ? false : null,
    includeDeleted: filters.includeDeleted,
    page,
    pageSize: Math.min(filters.pageSize > 0 ? filters.pageSize : 200, 200),
    sortBy: filters.sortBy,
    sortDescending: filters.sortDescending,
  }), [search, filters, page]);

  // Any filter change goes back to page 1.
  useEffect(() => { setPage(1); }, [search, filters.typeId, filters.status, filters.campus, filters.includeServe, filters.login, filters.membership, filters.includeDeleted, filters.pageSize]);

  const list = useQuery({
    queryKey: [...MEMBER_DIRECTORY_KEY, 'list', request],
    queryFn: () => memberService.listView(request),
    placeholderData: keepPreviousData,
  });

  // Quick counts for the summary cards (one row each; only the totals are used).
  const countOf = (extra: MemberFilterRequest) => ({
    queryKey: [...MEMBER_DIRECTORY_KEY, 'count', extra],
    queryFn: () => memberService.listView({ ...extra, page: 1, pageSize: 1 }).then((p) => p.totalCount),
    staleTime: 60_000,
  });
  const totalAll = useQuery(countOf({}));
  const totalMembers = useQuery(countOf({ isMember: true }));
  const totalVisitors = useQuery(countOf({ isMember: false }));
  const totalNoLogin = useQuery(countOf({ hasLogin: false }));

  const refresh = () => queryClient.invalidateQueries({ queryKey: MEMBER_DIRECTORY_KEY });

  const rows: Row[] = useMemo(() => (list.data?.items ?? []).map((m) => ({ ...m, id: m.memberID })), [list.data]);
  const total = list.data?.totalCount ?? 0;
  const anyFilter = !!(filters.search || filters.typeId || filters.status || filters.campus !== null || filters.login || filters.membership || filters.includeDeleted);

  const campusName = (id?: number | null, name?: string | null) => (id === null || id === undefined ? null : name || campuses.nameOf(id) || `Campus ${id}`);

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'name', label: 'Member', sortable: true,
      render: (_, m) => {
        const name = m.fullName || memberFullName(m) || `Member ${m.memberID}`;
        return (
          <Group gap="sm" wrap="nowrap">
            <Avatar src={m.avatar || undefined} radius="xl" size="md">{initialsOf(name)}</Avatar>
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Group gap={6} wrap="nowrap">
                <Text size="sm" fw={600} truncate>{name}</Text>
                {m.isDeleted && <Badge size="xs" color="red" variant="filled">{m.mergedIntoMemberID ? 'Merged' : 'Deleted'}</Badge>}
              </Group>
              <Text size="xs" c="dimmed" truncate>{[m.memberCode, m.email].filter(Boolean).join(' · ') || `ID ${m.memberID}`}</Text>
            </Stack>
          </Group>
        );
      },
    },
    {
      key: 'typeName', label: 'Type', width: 140,
      render: (_, m) => (m.typeName ? <Badge variant="light" color={m.typeColor && !m.typeColor.startsWith('#') ? m.typeColor : undefined} style={tint(m.typeColor)}>{m.typeName}</Badge> : <Text size="sm" c="dimmed">Not set</Text>),
    },
    {
      key: 'membershipStatus', label: 'Status', width: 150,
      render: (_, m) => { const s = membershipStatusFor(m); return <Badge variant="light" color={s.color}>{s.label}</Badge>; },
    },
    {
      key: 'homeLocationName', label: 'Campus', width: 170,
      render: (_, m) => {
        const home = campusName(m.homeLocationID, m.homeLocationName);
        const serve = m.serveLocationID !== m.homeLocationID ? campusName(m.serveLocationID, m.serveLocationName) : null;
        if (!home) return <Text size="sm" c="dimmed">Not set</Text>;
        return <Stack gap={0}><Text size="sm">{home}</Text>{serve && <Text size="xs" c="dimmed">Serves at {serve}</Text>}</Stack>;
      },
    },
    {
      key: 'cellNumber', label: 'Contact', width: 170,
      render: (_, m) => <Stack gap={0}><Text size="sm">{m.cellNumber || m.telephone || 'No number'}</Text>{m.altContact && <Text size="xs" c="dimmed">{m.altContact}</Text>}</Stack>,
    },
    {
      key: 'hasLogin', label: 'Links', width: 120,
      render: (_, m) => (
        <Group gap={4} wrap="nowrap">
          <Tooltip label={m.hasLogin || m.userId ? 'Has a login' : 'No login'}>
            <Badge size="sm" variant={m.hasLogin || m.userId ? 'light' : 'outline'} color={m.hasLogin || m.userId ? 'indigo' : 'gray'} px={6}>
              {m.hasLogin || m.userId ? <IconLink size={12} /> : <IconLinkOff size={12} />}
            </Badge>
          </Tooltip>
          {(m.isPlanningCentreLinked || m.planningCentrePersonId) && (
            <Tooltip label="Planning Centre"><Badge size="sm" variant="light" color="violet" px={6}><IconPlugConnected size={12} /></Badge></Tooltip>
          )}
          {(m.isAnatomyLinked || m.anatomyUserId) && (
            <Tooltip label="Anatomy"><Badge size="sm" variant="light" color="red" px={6}><IconHeartbeat size={12} /></Badge></Tooltip>
          )}
        </Group>
      ),
    },
    {
      key: 'ageGroup', label: 'Age group', width: 120,
      render: (_, m) => (m.ageGroup && m.ageGroup !== 'Unknown'
        ? <Badge variant="light" style={tint(m.ageGroupColor)}>{m.ageGroup}</Badge>
        : <Text size="sm" c="dimmed">Unknown</Text>),
    },
    {
      key: 'createdDateTime', label: 'On record since', width: 140, sortable: true,
      render: (_, m) => <Text size="sm">{formatStamp(m.createdDateTime)}</Text>,
    },
  ];

  const statCards = [
    { key: 'all', label: 'Everyone', value: totalAll.data, color: 'blue', icon: <IconUsers size={20} />, active: !filters.membership && !filters.login, onClick: () => setFilters({ membership: null, login: null }) },
    { key: 'member', label: 'Members', value: totalMembers.data, color: 'green', icon: <IconUserCheck size={20} />, active: filters.membership === 'member', onClick: () => setFilter('membership', filters.membership === 'member' ? null : 'member') },
    { key: 'visitor', label: 'Visitors', value: totalVisitors.data, color: 'gray', icon: <IconUserQuestion size={20} />, active: filters.membership === 'visitor', onClick: () => setFilter('membership', filters.membership === 'visitor' ? null : 'visitor') },
    { key: 'nologin', label: 'Without a login', value: totalNoLogin.data, color: 'orange', icon: <IconLinkOff size={20} />, active: filters.login === 'no', onClick: () => setFilter('login', filters.login === 'no' ? null : 'no') },
  ];

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <Group justify="space-between" wrap="wrap" align="flex-start">
            <Stack gap={0}>
              <Text size="xl" fw={700}>Members</Text>
              <Text c="dimmed" size="sm">Everyone the church knows: members, visitors and children, with or without a login.</Text>
            </Stack>
            <Group gap="sm">
              {canManageFields && (
                <Button variant="subtle" leftSection={<IconListDetails size={16} />} onClick={() => navigate('/admin/members/custom-fields')}>Member fields</Button>
              )}
              <Button variant="light" leftSection={<IconRefresh size={16} />} loading={list.isFetching && !list.isLoading} onClick={refresh}>Refresh</Button>
              {canCreate && <Button leftSection={<IconUserPlus size={16} />} onClick={() => setCreateOpen(true)}>Add member</Button>}
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 2, md: 4 }}>
            {statCards.map((c) => (
              <Card key={c.key} withBorder padding="md" radius="md" onClick={c.onClick}
                style={{ cursor: 'pointer', borderColor: c.active ? `var(--mantine-color-${c.color}-5)` : undefined, backgroundColor: c.active ? `var(--mantine-color-${c.color}-light)` : undefined }}>
                <Group wrap="nowrap">
                  <Badge circle size="xl" variant={c.active ? 'filled' : 'light'} color={c.color} style={{ width: 40, height: 40 }}>{c.icon}</Badge>
                  <Stack gap={0}>
                    <Text size="xl" fw={700}>{c.value === undefined ? '…' : c.value.toLocaleString('en-ZA')}</Text>
                    <Text size="xs" c="dimmed">{c.label}</Text>
                  </Stack>
                </Group>
              </Card>
            ))}
          </SimpleGrid>

          <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
              <Group gap="sm" wrap="wrap" align="flex-end">
                <TextInput style={{ flex: 1, minWidth: 220 }} placeholder="Search name, email, phone or member code" value={filters.search}
                  onChange={(e) => setFilter('search', e.currentTarget.value)} aria-label="Search members" />
                <Select w={170} placeholder="Any type" data={typeOptions} value={filters.typeId} onChange={(v) => setFilter('typeId', v)} clearable aria-label="Member type" />
                <Select w={150} placeholder="Active or not" data={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                  value={filters.status} onChange={(v) => setFilter('status', v)} clearable aria-label="Status" />
                <Select w={190} placeholder="Any campus" data={campuses.options} value={filters.campus} onChange={(v) => setFilter('campus', v)} clearable searchable aria-label="Campus" />
                <Select w={160} placeholder="Login or not" data={[{ value: 'yes', label: 'Has a login' }, { value: 'no', label: 'No login' }]}
                  value={filters.login} onChange={(v) => setFilter('login', v)} clearable aria-label="Login" />
                <Select w={160} placeholder="Members and visitors" data={[{ value: 'member', label: 'Members' }, { value: 'visitor', label: 'Visitors' }]}
                  value={filters.membership} onChange={(v) => setFilter('membership', v)} clearable aria-label="Membership" />
              </Group>
              <Group justify="space-between" wrap="wrap" gap="sm">
                <Group gap="lg" wrap="wrap">
                  <Switch size="sm" label="Include people who serve at this campus" checked={filters.includeServe} disabled={filters.campus === null}
                    onChange={(e) => setFilter('includeServe', e.currentTarget.checked)} />
                  <Switch size="sm" label="Show deleted and merged records" checked={filters.includeDeleted}
                    onChange={(e) => setFilter('includeDeleted', e.currentTarget.checked)} />
                </Group>
                <Group gap="sm">
                  {anyFilter && <Button variant="subtle" color="red" size="xs" leftSection={<IconFilterOff size={14} />} onClick={() => { clearFilters(); setPage(1); }}>Clear filters</Button>}
                  <Text size="sm" c="dimmed">{list.isLoading ? 'Loading' : `${total.toLocaleString('en-ZA')} ${total === 1 ? 'person' : 'people'}`}</Text>
                </Group>
              </Group>
            </Stack>
          </Paper>

          {list.isError && <Alert color="red" title="Could not load members">{errorMessage(list.error)}</Alert>}
          {filters.pageSize <= 0 && total > 200 && (
            <Alert color="blue">Showing the first 200 people. Narrow the filters or choose a page size to see the rest.</Alert>
          )}

          <Card withBorder p={0}>
            <DataTable<Row>
              columns={columns}
              data={rows}
              loading={list.isLoading}
              striped
              highlightOnHover
              paginationMode="server"
              enableInternalSearch={false}
              enableInternalSort={false}
              onSort={(key, dir) => setFilters({ sortBy: SORT_KEYS[key] ?? 'name', sortDescending: dir === 'desc' })}
              onRowClick={(m) => navigate(`/admin/members/${m.memberID}`)}
              emptyMessage={anyFilter ? 'Nobody matches these filters' : 'No members yet'}
              pagination={{
                page,
                pageSize: filters.pageSize,
                total,
                onPageChange: setPage,
                onPageSizeChange: (size) => setFilter('pageSize', size),
              }}
            />
          </Card>
        </Stack>
      </FadeIn>

      <CreateMemberModal opened={createOpen} onClose={() => setCreateOpen(false)} />
    </LayoutContainer>
  );
}
